import { useEffect, useRef, useState } from 'react';
import { drawGate, lineMiddle, type ZoneLine } from '@urbanrats/domain';
import { fmtAtomic, useCtx } from '../lib.ts';

interface ReplayView {
  gameRoundId: string;
  state: string;
  lastReason: string | null;
  finalCount: number | null;
  winning: string[];
  camera: { cameraId: string; name: string | null; city: string | null; countryCode: string | null; country: string | null } | null;
  line: { a: { x: number; y: number }; b: { x: number; y: number }; curve?: { x: number; y: number }; zone?: { a: { x: number; y: number }; b: { x: number; y: number } }; countedDirection: string };
  thresholds: { low: number; high: number } | null;
  odds: { selection: string; odds: { numerator: string; denominator: string } }[];
  windowS: number;
  media: { status: string; reason: string | null; playlistUrl: string | null } | null;
  events: { eventId: string; t: number | null; direction: string; vehicleClass: string; uncertain: boolean; counted: boolean }[];
  bets: { selection: string; currency: string; scale: number; n: number; stake: string; paid: string }[];
}

const SEL: { sel: string; label: string }[] = [
  { sel: 'UNDER', label: 'Few' },
  { sel: 'EXACT_LOW', label: 'Exactly L' },
  { sel: 'RANGE', label: 'Middle' },
  { sel: 'EXACT_HIGH', label: 'Exactly H' },
  { sel: 'OVER', label: 'Many' },
  { sel: 'EXACT', label: 'Either' },
];

/**
 * Round replay: the locked 60 s window as it was counted. Every counted crossing is a mark on the timeline
 * (click to jump); the count runs with the video; bets per selection and what they paid sit beside it.
 */
export function Replay({ roundId }: { roundId: string }) {
  const { backend } = useCtx();
  const [v, setV] = useState<ReplayView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [t, setT] = useState(0);
  const video = useRef<HTMLVideoElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const x = setTimeout(
      () =>
        void backend.server?.get<ReplayView>(`/v1/admin/rounds/${encodeURIComponent(roundId)}/replay`).then((r) => {
          if (r.ok) setV(r.value);
          else setError(r.error);
        }),
      0,
    );
    return () => clearTimeout(x);
  }, [backend, roundId]);
  useEffect(() => {
    const el = video.current;
    const url = v?.media?.playlistUrl;
    if (!el || !url || !backend.server) return;
    let hls: import('hls.js').default | null = null;
    let dead = false;
    void import('hls.js').then(({ default: Hls }) => {
      if (dead) return;
      const src = `${backend.server!.apiBase}${url}`;
      if (!Hls.isSupported()) el.src = src;
      else {
        hls = new Hls();
        hls.loadSource(src);
        hls.attachMedia(el);
      }
    });
    return () => {
      dead = true;
      hls?.destroy();
    };
  }, [v, backend]);
  // Counting gate + "+1" overlay on the video clock.
  useEffect(() => {
    let raf = 0;
    const draw = () => {
      raf = requestAnimationFrame(draw);
      const el = video.current;
      const cv = canvas.current;
      if (!el || !cv || !v || !el.videoWidth) return;
      const w = el.videoWidth;
      const h = el.videoHeight;
      if (cv.width !== w || cv.height !== h) {
        cv.width = w;
        cv.height = h;
      }
      const g = cv.getContext('2d')!;
      g.clearRect(0, 0, w, h);
      const px = Math.max(2, Math.round(w / 420));
      const now = el.currentTime;
      // The counting gate exactly as players saw it; the line flashes white on each counted crossing.
      let flash = 0;
      for (const e of v.events) if (e.counted && e.t !== null && now >= e.t && now - e.t < 0.35) flash = Math.max(flash, 1 - (now - e.t) / 0.35);
      drawGate(g, v.line as ZoneLine, w, h, { px, now, flash, still: el.paused });
      const m = lineMiddle(v.line);
      const font = Math.round(h / 12);
      g.font = `800 ${font}px 'Avenir Next Condensed', 'Arial Narrow', sans-serif`;
      g.textAlign = 'center';
      for (const e of v.events) {
        if (!e.counted || e.t === null) continue;
        const dt = now - e.t;
        if (dt < 0 || dt > 1) continue;
        g.globalAlpha = 1 - dt;
        g.lineWidth = Math.max(3, font / 6);
        g.strokeStyle = 'rgba(0,0,0,0.85)';
        g.strokeText('+1', m.x * w, m.y * h - dt * font * 1.2);
        g.fillStyle = '#ffd21f';
        g.fillText('+1', m.x * w, m.y * h - dt * font * 1.2);
        g.globalAlpha = 1;
      }
      setT((old) => (Math.abs(old - now) > 0.1 ? now : old));
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [v]);
  if (!backend.server) return null;
  if (!v) return <section className="card-x">{error ? <p className="warn-box">{error}</p> : <p className="muted">Loading the replay…</p>}</section>;
  const counted = v.events.filter((e) => e.counted && e.t !== null);
  const shown = counted.filter((e) => e.t! <= t).length;
  const currencies = [...new Set(v.bets.map((b) => b.currency))];
  const maxStake = Math.max(1, ...v.bets.map((b) => Number(b.stake)));
  const L = v.thresholds?.low;
  const H = v.thresholds?.high;
  const countOf: Record<string, string> = L !== undefined && H !== undefined ? { UNDER: `≤ ${L - 1}`, EXACT_LOW: `${L}`, RANGE: `${L + 1}–${H - 1}`, EXACT_HIGH: `${H}`, OVER: `≥ ${H + 1}`, EXACT: `${L} / ${H}` } : {};
  return (
    <section className="card-x replay" data-testid="round-replay">
      <header className="card-h">
        <h2>Replay</h2>
        {v.camera && (
          <span className="note">
            {v.camera.city}, {v.camera.country} · {v.camera.name}
          </span>
        )}
        <span className="spacer" />
        <span className="hud-inline">
          count <strong>{v.media?.playlistUrl ? shown : (v.finalCount ?? '—')}</strong>
          {v.finalCount !== null && <span className="muted"> / final {v.finalCount}</span>}
        </span>
      </header>
      <div className="replay-grid">
        <div>
          <div className="stage">
            {v.media?.playlistUrl ? (
              <>
                <video ref={video} controls playsInline muted className="replay-video" />
                <canvas ref={canvas} className="replay-canvas" />
              </>
            ) : (
              <div className="relay relay-off">{v.media ? `Window media ${v.media.status.toLowerCase()}${v.media.reason ? `: ${v.media.reason}` : ''}` : 'No locked window for this round'}</div>
            )}
          </div>
          <div className="replay-track" aria-label="Counted crossings over the window">
            {v.events
              .filter((e) => e.t !== null && e.t >= 0 && e.t <= v.windowS)
              .map((e) => (
                <button
                  key={e.eventId}
                  type="button"
                  className={`mark ${e.counted ? 'counted' : e.uncertain ? 'uncertain' : 'other'}`}
                  style={{ left: `${(100 * e.t!) / v.windowS}%` }}
                  title={`${e.t!.toFixed(1)} s · ${e.vehicleClass} · ${e.direction}${e.counted ? ' · counted' : e.uncertain ? ' · uncertain' : ' · not counted'}`}
                  onClick={() => {
                    if (video.current) video.current.currentTime = Math.max(0, e.t! - 1.5);
                    void video.current?.play();
                  }}
                />
              ))}
            <span className="playhead" style={{ left: `${Math.min(100, (100 * t) / v.windowS)}%` }} />
          </div>
          <div className="phase-labels">
            <span>0 s</span>
            <span>
              {counted.length} counted · {v.events.length - counted.length} other crossings
            </span>
            <span>{v.windowS} s</span>
          </div>
        </div>
        <div className="replay-side">
          <div className="result-box">
            <span className={`tag st-${v.state}`}>{v.state}</span>
            {v.state === 'VOIDED' ? <p className="warn-box">Refunded: {v.lastReason}</p> : v.winning.length > 0 && <p>Winning: {v.winning.join(', ')}</p>}
          </div>
          <ul className="sel-bars">
            {SEL.map(({ sel, label }) => {
              const o = v.odds.find((x) => x.selection === sel);
              if (!o) return null;
              const win = v.winning.includes(sel);
              return (
                <li key={sel} className={win ? 'win' : ''}>
                  <span className="sel-name">
                    {label} <strong>{countOf[sel]}</strong> <span className="muted">×{(Number(o.odds.numerator) / Number(o.odds.denominator)).toFixed(2)}</span>
                  </span>
                  {currencies.map((c) => {
                    const b = v.bets.find((x) => x.selection === sel && x.currency === c);
                    return (
                      <span key={c} className="sel-bar">
                        <span style={{ width: `${b ? (100 * Number(b.stake)) / maxStake : 0}%` }} />
                        <em>{b ? `${b.n} · ${fmtAtomic(BigInt(b.stake), b.scale, c)}${BigInt(b.paid) > 0n ? ` → ${fmtAtomic(BigInt(b.paid), b.scale, c)}` : ''}` : `0 · ${c}`}</em>
                      </span>
                    );
                  })}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}
